import { useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import {
    startStreaming,
    appendSuperAgentToken,
    addAgentPanel,
    appendAgentToken,
    completeAgentPanel,
    setLiveStatus,
    finalizeStreaming,
    setStreamingError,
} from '../../redux/features/streaming/streamingSlice';
import {
    addMessageToConversationFromUser,
    addMessageToConversationFromServer,
    updateBotResponseStatus,
    editConversationTitle,
} from '../../redux/features/conversations/conversationsSlice';
import { AuthHelper } from '../auth/AuthHelper';
import { AuthorRoles, ChatMessageType, IChatMessage, AgentPanelData } from '../models/ChatMessage';
import { StreamingChatService, Citation as StreamingCitation } from '../services/StreamingChatService';

const streamingChatService = new StreamingChatService();

export const useStreamingChat = () => {
    const dispatch = useAppDispatch();
    const { instance, inProgress } = useMsal();
    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);
    const abortControllerRef = useRef<AbortController | null>(null);
    // Accumulate content in a ref so the onDone callback has the full text
    const contentBufferRef = useRef<string>('');
    const agentPanelsRef = useRef<AgentPanelData[]>([]);

    const sendStreamingMessage = useCallback(
        async (chatId: string, agentId: string, message: string) => {
            contentBufferRef.current = '';
            agentPanelsRef.current = [];

            // Add user message to conversation
            const chatInput: IChatMessage = {
                chatId,
                timestamp: new Date().getTime(),
                userId: activeUserInfo?.id as string,
                userName: activeUserInfo?.email.toUpperCase() as string,
                content: message,
                type: ChatMessageType.Message,
                authorRole: AuthorRoles.User,
            };
            dispatch(addMessageToConversationFromUser({ message: chatInput, chatId }));
            dispatch(updateBotResponseStatus({ chatId, status: 'Yanıt oluşturuluyor...' }));
            dispatch(startStreaming({ chatId }));

            abortControllerRef.current = new AbortController();

            try {
                const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);

                await streamingChatService.streamBotResponse(
                    chatId,
                    agentId,
                    message,
                    accessToken,
                    {
                        onToken: (content) => {
                            contentBufferRef.current += content;
                            dispatch(appendSuperAgentToken({ chatId, token: content }));
                        },
                        onAgentStart: (agentName) => {
                            dispatch(addAgentPanel({ chatId, agentName }));
                            agentPanelsRef.current.push({
                                agentName,
                                status: 'running',
                                content: '',
                                startTime: Date.now(),
                            });
                        },
                        onAgentToken: (agentName, content) => {
                            dispatch(appendAgentToken({ chatId, agentName, token: content }));
                            const panel = agentPanelsRef.current.find((p) => p.agentName === agentName);
                            if (panel) { panel.content += content; }
                        },
                        onAgentDone: (agentName) => {
                            dispatch(completeAgentPanel({ chatId, agentName }));
                            const panel = agentPanelsRef.current.find((p) => p.agentName === agentName);
                            if (panel) {
                                panel.status = 'completed';
                                panel.endTime = Date.now();
                                panel.durationSec = parseFloat(((panel.endTime - panel.startTime) / 1000).toFixed(1));
                            }
                        },
                        onStatus: (label) => {
                            dispatch(setLiveStatus({ chatId, label }));
                        },
                        onDone: (citations: StreamingCitation[], module: string, title?: string, messageId?: string) => {
                            dispatch(finalizeStreaming({ chatId, citations, module }));
                            dispatch(updateBotResponseStatus({ chatId, status: undefined }));
                            if (title) {
                                dispatch(editConversationTitle({ id: chatId, newTitle: title }));
                            }

                            // Convert streaming result to a permanent bot message.
                            const botMessage: IChatMessage = {
                                ...(messageId ? { id: messageId } : {}),
                                chatId,
                                timestamp: new Date().getTime(),
                                userId: 'Bot',
                                userName: 'Bot',
                                content: contentBufferRef.current,
                                type: ChatMessageType.Message,
                                authorRole: AuthorRoles.Bot,
                                citations: citations.map((c) => ({
                                    link: c.url ?? '',
                                    sourceName: c.title ?? '',
                                    snippet: c.content ?? '',
                                    relevanceScore: 0,
                                })),
                                agentPanels: agentPanelsRef.current.length > 0 ? [...agentPanelsRef.current] : undefined,
                            };
                            dispatch(addMessageToConversationFromServer({ message: botMessage, chatId }));
                        },
                        onError: (error) => {
                            dispatch(setStreamingError({ chatId, error }));
                            dispatch(updateBotResponseStatus({ chatId, status: undefined }));
                            // Show error as a bot message so it's visible in chat
                            const errorMessage: IChatMessage = {
                                chatId,
                                timestamp: new Date().getTime(),
                                userId: 'Bot',
                                userName: 'Bot',
                                content: `⚠️ ${error}`,
                                type: ChatMessageType.Message,
                                authorRole: AuthorRoles.Bot,
                            };
                            dispatch(addMessageToConversationFromServer({ message: errorMessage, chatId }));
                        },
                    },
                    abortControllerRef.current.signal,
                );
            } catch (e: unknown) {
                const err = e as Error;
                if (err.name !== 'AbortError') {
                    const errorText = err.message || 'Streaming hatası oluştu.';
                    dispatch(setStreamingError({ chatId, error: errorText }));
                    // Show error as a bot message so it's visible in chat
                    const errorMessage: IChatMessage = {
                        chatId,
                        timestamp: new Date().getTime(),
                        userId: 'Bot',
                        userName: 'Bot',
                        content: `⚠️ ${errorText}`,
                        type: ChatMessageType.Message,
                        authorRole: AuthorRoles.Bot,
                    };
                    dispatch(addMessageToConversationFromServer({ message: errorMessage, chatId }));
                }
                dispatch(updateBotResponseStatus({ chatId, status: undefined }));
            }
        },
        [dispatch, instance, inProgress, activeUserInfo],
    );

    const cancelStreaming = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, []);

    return { sendStreamingMessage, cancelStreaming };
};
